import { Box, Link, Stack, Text, system, SystemProps } from "flicket-ui";
import React, { FC, useContext, useState } from "react";
import styled from "styled-components";
import { marketingUrl } from "~config";
import { UserContext } from "~context";
import { useOrganization, useBottomNavVisible } from "~hooks";
import { pick } from "@styled-system/props";
import { CookiePreferencesModal } from "~components/CookiesPreferences/CookiePreferencesModal";
import { FixedSizeImage } from "./Image";
import { LINK_EXTERNAL_FAQ_DOMAIN } from "~lib/helpers/constants";

interface FooterProps extends SystemProps {
  hasFullPageBackground?: boolean;
  narrow?: boolean;
}

const StyledFooter = styled(Box)<{
  $narrow: boolean;
  $hasBottomNav: boolean;
  $hasFullPageBackground?: boolean;
}>`
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 24px 16px;
  padding-bottom: ${(props) => (props.$hasBottomNav ? "130px" : "80px")};
  background-color: ${(p) => p.theme.colors.N800};

  color: white;

  font-size: ${(p) => p.theme.fontSizes[2]};
  font-weight: ${(p) => p.theme.fontWeights.regular};
  line-height: 150%;
  min-height: 160px;

  > div {
    display: flex;
    justify-content: space-between;
    position: relative;
    width: 100%;
    max-width: ${(p) => (p.$narrow ? 940 : 1184)}px;
  }

  /* When on desktop and there is a fullpage backgroun image give 
     the footer a faint transaprency and blur */
  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    backdrop-filter: blur(8px);
    background-color: rgba(0, 0, 0, 0.8);
  }

  ${system}
`;

export const Footer: FC<FooterProps> = ({
  hasFullPageBackground,
  narrow = false,
  ...props
}) => {
  const { user } = useContext(UserContext);
  const { organization } = useOrganization();
  const [isCookieModalOpen, setIsCookieModalOpen] = useState(false);
  const hasBottomNav = useBottomNavVisible();

  const cookieBannerEnabled = Boolean(organization?.cookieConsent);

  return (
    <StyledFooter
      display="flex"
      {...pick(props)}
      $narrow={narrow}
      $hasBottomNav={hasBottomNav}
      $hasFullPageBackground={hasFullPageBackground}
    >
      <Stack
        direction={{ _: "vertical", sm: "horizontal" }}
        alignItems={"center"}
        flexWrap="nowrap"
        justifyContent="space-between"
        flexShrink={0}
        gap={[7, 7, 7, 0]}
        p={2}
      >
        <Stack
          direction={{ _: "vertical", sm: "horizontal" }}
          flexWrap="nowrap"
          flexShrink={0}
          alignItems={"center"}
          justifyContent={{ _: "center", sm: "flex-start" }}
          gap={[4, 4, 4, 2]}
          textAlign={{ _: "center", md: "left" }}
        >
          <Text variant="regular" color="white">
            <Link to="/privacy-policy" color="white">
              Privacy policy
            </Link>
          </Text>
          <Text variant="regular" color="white">
            <Link to="/terms-and-conditions" color="white">
              Terms and conditions
            </Link>
          </Text>
          <Text variant="regular" color="white">
            <Link to={user ? "/data-access" : "/login"} color="white">
              My data
            </Link>
          </Text>

          {cookieBannerEnabled && (
            <Text variant="regular" color="white">
              <Link onClick={() => setIsCookieModalOpen(true)} color="white">
                Cookie Preferences
              </Link>
            </Text>
          )}

          <Text variant="regular" color="white">
            <Link
              target="_blank"
              href={organization?.helpLink ?? LINK_EXTERNAL_FAQ_DOMAIN}
              rel="noopener noreferrer"
            >
              Help
            </Link>
          </Text>
        </Stack>

        <Stack
          alignItems="center"
          direction={{ _: "vertical", sm: "horizontal" }}
          gap={1}
        >
          <Text variant="small" color="N500" display="inline">
            Powered by
          </Text>
          <Link href={marketingUrl}>
            <FixedSizeImage
              alt="Flicket Ltd"
              src={"/static/flicket-logo-footer.svg"}
              width={88}
              height={12}
            />
          </Link>
        </Stack>
      </Stack>

      {cookieBannerEnabled && (
        <CookiePreferencesModal
          isOpen={isCookieModalOpen}
          onClose={() => setIsCookieModalOpen(false)}
        />
      )}
    </StyledFooter>
  );
};
